import React, {useState, useEffect} from 'react';
import AdministratorWizardBody from './AdministratorWizardBody';
import {AdministratorCreateSchema} from '../../../api/DBSchema/Administrator';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes} from '../../../api/consts';
import {useSMCSnackbar} from '../../../utils/hooks';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import SMCModal from '../../../components/core/SMCModal';
import {useTranslation} from 'react-i18next';
import {useIsMountedRef} from '../../../utils/hooks';

function AdministratorWizard({
    isOpen,
    onRequestClose,
    onSuccess
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar } = useSMCSnackbar();
    const isMountedRef = useIsMountedRef();

    const [isLoading, setIsLoading] = useState(false);
    const [isTwoStepAuthenticationEnabled, setIsTwoStepAuthenticationEnabled] = useState(false);

    const [uid, setUid] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState(AdministratorCreateSchema.roleValues.administrator);
    const [password, setPassword] = useState('');

    const onFinish = async () => {
        const createUserRequestObj = {
            [AdministratorCreateSchema.name]: uid,
            [AdministratorCreateSchema.firstName]: firstName,
            [AdministratorCreateSchema.lastName]: lastName,
            [AdministratorCreateSchema.email]: email,
            [AdministratorCreateSchema.role]: role,
            [AdministratorCreateSchema.password]: password,
        }
        setIsLoading(true);
        Api.createObject(ObjectTypes.ADMINISTRATOR, createUserRequestObj)
        .then(() => {
            if (isMountedRef.current) {
                onSuccess(uid);
            }
        })
        .catch (e => {
            if (isMountedRef.current) {
                const errorMessage = getErrorMessage(e);
                alert(errorMessage);
            }
        })
        .finally(() => {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        });
    }

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const sessData = await Api.getSessionData({
                    attributes: [
                        SessionDataGetSchema.isSystemTwoFactorAuthenticationEnabled
                    ]
                });
                const isSystemTwoFactorAuthenticationEnabled = sessData.data.isSystemTwoFactorAuthenticationEnabled;
                setIsTwoStepAuthenticationEnabled(!!isSystemTwoFactorAuthenticationEnabled);
            } catch (e) {
                const errorMessage = getErrorMessage(e);
                enqueueErrorSnackbar(errorMessage);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [getErrorMessage, enqueueErrorSnackbar]);

    // Reset to defaults on every open
    useEffect(() => {
        if (isOpen) {
            setUid('');
            setFirstName('');
            setLastName('');
            setEmail('');
            setRole(AdministratorCreateSchema.roleValues.administrator);
            setPassword('');
        }
    }, [isOpen]);

    const wizardVars = {
        uid, setUid,
        firstName, setFirstName,
        lastName, setLastName,
        email, setEmail,
        role, setRole,
        password, setPassword,
        isTwoStepAuthenticationEnabled
    }

    return (
        <SMCModal
            title={t('SMP.CREATE_NEW_ADMIN.HEADING')}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={610}
            height={600}
            loading={isLoading}
        >
            <AdministratorWizardBody
                onFinish={onFinish}
                onCancel={onRequestClose}
                wizardVars={wizardVars}
            />
        </SMCModal>
    );
}

export default AdministratorWizard;